import React, {useEffect, useRef, useState, useContext, useMemo} from 'react'
import {useLocation} from 'react-router-dom'
import {IconButton} from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import AddRoundedIcon from '@material-ui/icons/AddRounded'
import PopAddViewOptions from './PopAddViewOptions'
import PopFilteredView from './PopFilteredView'
import PopGroupView from './PopGroupView'
import './Views.scss'
import _ from 'lodash'
import {Colors} from '../ColorPicker'

// custom components
import GroupViewItem from './GroupViewItem'
import useCalendarViewContext from '../../../context/CalendarView'
import LoadingIndicator from '../../ReservationConfiguration/LoadingIcon'
import {ReservationCalendarMainContext} from '../../../context/ReservationCalendarMain'
import {ReservationContext} from '../../../context/Reservation'
import useReservationGanttContext from '../../../context/ReservationGantt/ReservationGantt'

const Views = () => {
  const {setGroupBy, setFilters} = useReservationGanttContext()
  const {
    views,
    groups,
    loadCalendarViews,
    viewData,
    setViewData,
    setCurrentView,
    setShareableViewData,
  } = useCalendarViewContext()
  const {calendarView} = useContext(ReservationCalendarMainContext)
  const {clearReservation} = useContext(ReservationContext)

  const [anchorEl, setAnchorEl] = useState(false)
  const [showPopFilteredView, setShowPopFilteredView] = useState(null)
  const [showPopGroupView, setShowPopGroupView] = useState(null)
  const refHead = useRef(null)
  const [viewExpand, setViewExpand] = useState(false)
  const useQuery = useLocation()

  const nextColor = useMemo(() => {
    let _colors = []
    _.each(views.data, v => {
      _colors.push(v.group_view.color)
      _.each(v.group_view.calendar_child_view, cv => {
        _colors.push(cv.color)
      })
    })
    const _Colors = _.mapValues(_.groupBy(Colors), c => 0)
    const _groupColors = _.mapValues(_.groupBy(_colors), c => c.length)
    const sort = _.sortBy(
      _.map({..._Colors, ..._groupColors}, (v, k) => ({k, v})),
      c => c.v
    )
    return sort[0].k
  }, [views])

  const customColors = useMemo(() => {
    let _colors = []
    _.each(views.data, v => {
      _colors.push(v.group_view.color)
      _.each(v.group_view.calendar_child_view, cv => {
        _colors.push(cv.color)
      })
    })
    return _.difference(_.uniq(_colors), Colors)
  }, [views])

  const handleClickView = (e, view) => {
    setFilters([])
    setViewData(data => {
      let _selectedFilteredViews = data.selectedFilteredViews

      if (
        view.group_view.calendar_child_view.indexOf(f =>
          viewData?.selectedFilteredViews?.forEach(v => {
            if (v._id['$oid'] === f?._id['$oid']) {
              return v
            }
          })
        ) === -1
      ) {
        _selectedFilteredViews = []
      }

      // check if same view
      if (data.selectedView === view) {
        // if has child or child exists
        if (!!data.selectedFilteredView) {
          return {
            ...data,
            selectedFilteredViews: [],
          }
        } else {
          setGroupBy(null)
          clearReservation()
          return {
            selectedView: {},
            selectedFilteredViews: [],
          }
        }
      }
      setGroupBy(null)
      clearReservation()
      return {selectedView: view, selectedFilteredViews: _selectedFilteredViews}
    })
    setCurrentView('')
    setShareableViewData({is_shareable_view: false})
  }

  const handleClickFilteredView = (e, fview, view) => {
    setViewData(prevData => {
      let newSelectedViews = [...prevData.selectedFilteredViews]
      const isSelected = newSelectedViews.some(f => f._id === fview._id)
      if (isSelected) {
        newSelectedViews = newSelectedViews.filter(f => f._id !== fview._id)
      } else {
        newSelectedViews = newSelectedViews.filter(f => f.entity_id === fview.entity_id)
        newSelectedViews.push(fview)
      }

      const updatedSelectedView =
        view.group_view.entity_id !== prevData?.selectedView?.group_view?.entity_id
          ? view
          : prevData.selectedView

      return {
        ...prevData,
        selectedFilteredViews: newSelectedViews,
        selectedView: updatedSelectedView,
      }
    })
    setCurrentView('')
    setShareableViewData({is_shareable_view: false})
  }

  const handleClickAddView = () => {
    setAnchorEl(refHead.current)
  }

  const handleClickAddGroupView = () => {
    setShowPopGroupView({
      entity_id: -1,
      color: nextColor,
      groups: [],
      actions: [],
    })
    setAnchorEl(null)
  }

  const handleClickAddChildView = () => {
    setShowPopFilteredView({
      entity_id: -1,
      color: nextColor,
      filters: [],
      name: '',
    })
    setAnchorEl(null)
  }

  const handleAddSuccess = () => {
    setShowPopGroupView(null)
    loadCalendarViews()
  }

  const handleAddChildSuccess = () => {
    setShowPopFilteredView(null)
    loadCalendarViews()
  }

  useEffect(() => {
    if (useQuery.search === '?from_welcome') {
      handleClickAddGroupView()
    }
    //eslint-disable-next-line
  }, [])

  const onEditView = group_view => {
    setShowPopGroupView(group_view)
    setShareableViewData({is_shareable_view: false})
  }
  const onEditFilterView = (e, d) => {
    setShowPopFilteredView(d)
    setShareableViewData({is_shareable_view: false})
  }

  return (
    <div style={{marginTop: 16}} hidden={calendarView === 'reservation'}>
      <div
        ref={refHead}
        className="ap-flex ap-flex--align-center"
        style={{padding: '0 16px 0 16px'}}
      >
        <div className="ap-flex">
          <span style={{color: '#596175'}} className="ap-typography ap-typography--lg">
            Views
          </span>
        </div>
        <div className="ap-flex ap-flex--end">
          <IconButton
            size="small"
            className="ap-icon-button ap-icon-button--square"
            style={{color: '#596175', marginRight: 4}}
            onClick={handleClickAddView}
          >
            <AddRoundedIcon />
          </IconButton>

          <IconButton
            size="small"
            className="ap-icon-button ap-icon-button--square"
            style={{color: '#596175', marginLeft: 4}}
            onClick={() => setViewExpand(!viewExpand)}
          >
            {viewExpand ? <ChevronLeftIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </div>
      </div>
      <div className="c-calendar-views__list" hidden={viewExpand}>
        {views.isLoading && <LoadingIndicator />}
        {!views.isLoading &&
          _.map(views.data, (e, i) => (
            <GroupViewItem
              key={i}
              data={e}
              groups={groups}
              selectedView={viewData.selectedView}
              selectedFilteredView={viewData.selectedFilteredViews}
              onClickView={handleClickView}
              onClickFilteredView={handleClickFilteredView}
              onClickEdit={ev => onEditView(e.group_view)}
              onClickEditFilteredView={(e, d) => onEditFilterView(e, d)}
            />
          ))}
      </div>
      <PopAddViewOptions
        open={!!anchorEl}
        anchorEl={anchorEl}
        views={views.data}
        onClose={e => setAnchorEl(null)}
        onClickAddChildView={handleClickAddChildView}
        onClickAddGroupView={handleClickAddGroupView}
      />
      <PopFilteredView
        open={Boolean(showPopFilteredView)}
        data={showPopFilteredView || {}}
        groups={groups}
        customColors={customColors}
        onClose={() => setShowPopFilteredView(null)}
        onSuccess={handleAddChildSuccess}
        viewData={views?.data || []}
      />
      <PopGroupView
        open={Boolean(showPopGroupView)}
        data={showPopGroupView || {}}
        groups={groups}
        customColors={customColors}
        onClose={() => setShowPopGroupView(null)}
        onSuccess={handleAddSuccess}
        viewData={views?.data || []}
      />
    </div>
  )
}

export default Views
